// ── Aim Assist System ──
// Four techniques for mobile aim assistance:
// A. Friction/Slowdown — reduce camera turn speed near enemies
// B. ADS Snap — snap crosshair toward nearest enemy on ADS press
// C. Rotation Assist — subtle drift toward target when actively aiming
// D. Bullet Magnetism — expand enemy hitbox for mobile hit-scan
// Plus per-scope sensitivity multipliers.

// ── Types ──

export interface Vec2 {
  x: number;
  y: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface Box {
  min: Vec3;
  max: Vec3;
}

export interface EnemyInfo {
  /** World position of the enemy. */
  position: Vec3;
  /** Screen-space position (normalized, -1 to 1 or 0-centered). */
  screenPos: Vec2;
  /** Distance from the player camera in world units (metres). */
  distance: number;
  /** Whether the enemy is currently visible (not occluded). */
  isVisible: boolean;
}

// ── Scope Types ──

export const enum ScopeType {
  HipFire = 'hipfire',
  RedDot = 'reddot',
  Scope2x = 'scope2x',
  Scope4x = 'scope4x',
  Scope6x = 'scope6x',
}

export const DEFAULT_SCOPE_SENSITIVITY: Record<string, number> = {
  [ScopeType.HipFire]: 1.0,
  [ScopeType.RedDot]: 0.8,
  [ScopeType.Scope2x]: 0.6,
  [ScopeType.Scope4x]: 0.3,
  [ScopeType.Scope6x]: 0.2,
};

// ── Settings shape expected by AimAssist ──

export interface AimAssistSettings {
  aimAssistStrength: number;
  adsSnapEnabled: boolean;
  scopeSensitivity: Record<string, number>;
}

// ── Constants ──

/** Maximum range (metres) at which friction and rotation assist apply. */
const MAX_ASSIST_RANGE = 50;

/** Inner zone radius in normalized screen units (~1m radius at mid-range). */
const INNER_ZONE_RADIUS = 0.1;
/** Outer zone radius in normalized screen units (~2.5m radius at mid-range). */
const OUTER_ZONE_RADIUS = 0.25;

/** Minimum friction multiplier (inner zone, full strength). */
const MIN_FRICTION = 0.3;
/** Friction at the edge of outer zone. */
const OUTER_FRICTION = 0.5;

/** Maximum screen-space distance for rotation assist engagement. */
const ROTATION_ASSIST_RANGE = 0.3;
/** Base rotation assist strength factor. */
const ROTATION_ASSIST_FACTOR = 0.03;

/** ADS snap cone threshold: tan(15 degrees) ≈ 0.2679. */
const ADS_SNAP_CONE = Math.tan((15 * Math.PI) / 180);

/** Number of frames to smooth ADS snap over (2-3 frames at 60fps). */
const SNAP_SMOOTH_FRAMES = 3;
/** Snap interpolation speed (exponential decay factor per second). */
const SNAP_DECAY_RATE = 15;

/** Bullet magnetism hitbox expansion factor for mobile. */
const HITBOX_EXPANSION = 1.2;

// ── AimAssist Class ──

export class AimAssist {
  private strength: number;
  private adsSnapEnabled: boolean;
  private scopeSensitivity: Record<string, number>;

  // ADS snap smoothing state
  private snapRemaining: Vec2 = { x: 0, y: 0 };
  private snapActive = false;

  constructor(settings: AimAssistSettings) {
    this.strength = settings.aimAssistStrength;
    this.adsSnapEnabled = settings.adsSnapEnabled;
    this.scopeSensitivity = { ...settings.scopeSensitivity };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // A. Friction / Slowdown
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Calculate the friction multiplier for camera turn speed.
   * Returns a value between MIN_FRICTION and 1.0.
   * @param crosshairPos - Current crosshair position in normalized screen space.
   * @param enemies - Array of visible enemy info.
   * @param range - Distance to the closest enemy being considered (or player look range).
   * @returns Multiplier to apply to camera turn speed (1.0 = no slowdown).
   */
  getFrictionMultiplier(crosshairPos: Vec2, enemies: EnemyInfo[], range: number): number {
    if (this.strength === 0) return 1.0;
    if (enemies.length === 0) return 1.0;

    let bestMultiplier = 1.0;

    for (const enemy of enemies) {
      if (!enemy.isVisible) continue;
      if (enemy.distance > MAX_ASSIST_RANGE) continue;

      const dx = crosshairPos.x - enemy.screenPos.x;
      const dy = crosshairPos.y - enemy.screenPos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      let multiplier = 1.0;

      if (dist <= INNER_ZONE_RADIUS) {
        // Full slowdown in inner zone
        multiplier = MIN_FRICTION;
      } else if (dist <= OUTER_ZONE_RADIUS) {
        // Lerp from full slowdown to partial slowdown in outer zone
        const t = (dist - INNER_ZONE_RADIUS) / (OUTER_ZONE_RADIUS - INNER_ZONE_RADIUS);
        multiplier = MIN_FRICTION + t * (OUTER_FRICTION - MIN_FRICTION);
      } else {
        continue; // Outside both zones, no friction
      }

      // Apply strength scaling: lerp between 1.0 (no effect) and multiplier (full effect)
      multiplier = 1.0 - this.strength * (1.0 - multiplier);

      if (multiplier < bestMultiplier) {
        bestMultiplier = multiplier;
      }
    }

    return bestMultiplier;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // B. ADS Snap
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Find the best ADS snap target within a cone.
   * Returns the screen position to snap toward, or null if no valid target.
   * @param crosshairPos - Current crosshair position.
   * @param enemies - Array of enemy info.
   * @param maxAngle - Maximum cone angle in degrees.
   * @returns Target screen position to snap to, or null.
   */
  getAdsSnapTarget(crosshairPos: Vec2, enemies: EnemyInfo[], maxAngle: number): Vec2 | null {
    if (this.strength === 0) return null;
    if (!this.adsSnapEnabled) return null;
    if (enemies.length === 0) return null;

    const coneThreshold = Math.tan((maxAngle * Math.PI) / 180);

    let bestTarget: Vec2 | null = null;
    let bestDistance = Infinity;

    for (const enemy of enemies) {
      if (!enemy.isVisible) continue;

      const dx = enemy.screenPos.x - crosshairPos.x;
      const dy = enemy.screenPos.y - crosshairPos.y;
      const screenDist = Math.sqrt(dx * dx + dy * dy);

      // Check if within cone
      if (screenDist > coneThreshold) continue;

      // Pick closest enemy by world distance
      if (enemy.distance < bestDistance) {
        bestDistance = enemy.distance;
        bestTarget = { x: enemy.screenPos.x, y: enemy.screenPos.y };
      }
    }

    return bestTarget;
  }

  /**
   * Start an ADS snap animation toward the given target position.
   * The snap will be smoothed over SNAP_SMOOTH_FRAMES via update().
   */
  startAdsSnap(targetDelta: Vec2): void {
    this.snapRemaining = { x: targetDelta.x, y: targetDelta.y };
    this.snapActive = true;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // C. Rotation Assist
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Calculate a subtle rotation delta that drifts the camera toward the nearest target.
   * Only engages when the player is actively aiming (aimVelocity > 0).
   * @param crosshairPos - Current crosshair position.
   * @param enemies - Array of enemy info.
   * @param aimVelocity - Current aim velocity (player input direction).
   * @returns Delta to add to camera rotation.
   */
  getRotationDelta(crosshairPos: Vec2, enemies: EnemyInfo[], aimVelocity: Vec2): Vec2 {
    if (this.strength === 0) return { x: 0, y: 0 };

    // Only engage when player is actively aiming
    const aimSpeed = Math.sqrt(aimVelocity.x * aimVelocity.x + aimVelocity.y * aimVelocity.y);
    if (aimSpeed === 0) return { x: 0, y: 0 };
    if (enemies.length === 0) return { x: 0, y: 0 };

    let bestDx = 0;
    let bestDy = 0;
    let bestScore = Infinity;

    for (const enemy of enemies) {
      if (!enemy.isVisible) continue;
      if (enemy.distance > MAX_ASSIST_RANGE) continue;

      const dx = enemy.screenPos.x - crosshairPos.x;
      const dy = enemy.screenPos.y - crosshairPos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > ROTATION_ASSIST_RANGE) continue;
      if (dist === 0) continue; // Already on target

      // Score: closer on screen = stronger candidate
      if (dist < bestScore) {
        bestScore = dist;
        // Direction toward enemy, scaled by distance (proportional)
        // Normalize direction and scale by factor
        const invDist = 1 / dist;
        const strength = ROTATION_ASSIST_FACTOR * this.strength * Math.min(aimSpeed, 1);
        // Closer targets get less drift (already nearly aimed), farther get more
        // Use dist as the proportional factor
        bestDx = dx * invDist * strength * dist;
        bestDy = dy * invDist * strength * dist;
      }
    }

    return { x: bestDx, y: bestDy };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // D. Bullet Magnetism
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Expand an enemy hitbox for mobile clients.
   * Desktop clients get the original hitbox unchanged.
   * @param hitbox - The original axis-aligned bounding box.
   * @param isMobile - Whether the current client is mobile.
   * @returns The (potentially expanded) hitbox.
   */
  expandHitbox(hitbox: Box, isMobile: boolean): Box {
    if (!isMobile || this.strength === 0) {
      return {
        min: { x: hitbox.min.x, y: hitbox.min.y, z: hitbox.min.z },
        max: { x: hitbox.max.x, y: hitbox.max.y, z: hitbox.max.z },
      };
    }

    // Interpolate expansion factor based on strength: 1.0 (no expand) to HITBOX_EXPANSION (full)
    const factor = 1.0 + (HITBOX_EXPANSION - 1.0) * this.strength;

    // Calculate center and half-extents
    const cx = (hitbox.min.x + hitbox.max.x) / 2;
    const cy = (hitbox.min.y + hitbox.max.y) / 2;
    const cz = (hitbox.min.z + hitbox.max.z) / 2;

    const hx = (hitbox.max.x - hitbox.min.x) / 2;
    const hy = (hitbox.max.y - hitbox.min.y) / 2;
    const hz = (hitbox.max.z - hitbox.min.z) / 2;

    return {
      min: {
        x: cx - hx * factor,
        y: cy - hy * factor,
        z: cz - hz * factor,
      },
      max: {
        x: cx + hx * factor,
        y: cy + hy * factor,
        z: cz + hz * factor,
      },
    };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Per-Scope Sensitivity
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Get the sensitivity multiplier for a given scope type.
   * @param scopeType - The scope type string.
   * @returns Sensitivity multiplier (1.0 = hip-fire baseline).
   */
  getScopeSensitivity(scopeType: string): number {
    return this.scopeSensitivity[scopeType] ?? 1.0;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Update (ADS Snap Smoothing)
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Per-frame update for ADS snap smoothing.
   * Returns the delta to apply this frame. Smooths over 2-3 frames.
   * @param dt - Delta time in seconds.
   * @returns Camera delta to apply this frame.
   */
  update(dt: number): Vec2 {
    if (!this.snapActive) return { x: 0, y: 0 };

    // Exponential decay: apply a portion of remaining snap each frame
    const factor = 1 - Math.exp(-SNAP_DECAY_RATE * dt);
    const dx = this.snapRemaining.x * factor;
    const dy = this.snapRemaining.y * factor;

    this.snapRemaining.x -= dx;
    this.snapRemaining.y -= dy;

    // Check if snap is essentially complete
    const remaining = Math.abs(this.snapRemaining.x) + Math.abs(this.snapRemaining.y);
    if (remaining < 0.0001) {
      // Apply any tiny remainder and deactivate
      const finalDx = dx + this.snapRemaining.x;
      const finalDy = dy + this.snapRemaining.y;
      this.snapRemaining.x = 0;
      this.snapRemaining.y = 0;
      this.snapActive = false;
      return { x: finalDx, y: finalDy };
    }

    return { x: dx, y: dy };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Dispose
  // ═══════════════════════════════════════════════════════════════════════

  /** Clean up internal state. */
  dispose(): void {
    this.snapRemaining = { x: 0, y: 0 };
    this.snapActive = false;
  }
}
